import { useCallback, useState } from 'react'
import Toast from '@/components/elements/toast'

interface UsePaginationProps {
	initialLimit?: number
	initialOffset?: number
	initialPage?: number
}

interface UsePaginationReturn {
	offset: number
	limit: number
	currentPage: number
	isFirstPage: boolean
	nextPage: () => void
	prevPage: () => void
	goToPage: (page: number) => void
	setItemsPerPage: (limit: number) => void
	counterPages: number
	isLoading: boolean
	setIsLoading: (loading: boolean) => void
	handleEmptyResponse: () => void
}

export const usePagination = ({
	initialLimit = 10,
	initialOffset = 0,
	initialPage = 1,
}: UsePaginationProps): UsePaginationReturn => {
	const [limit, setLimit] = useState<number>(initialLimit)
	const [offset, setOffset] = useState<number>(initialOffset)
	const [counterPages, setCounterPages] = useState<number>(initialPage)
	const [isLoading, setIsLoading] = useState<boolean>(false)

	const isFirstPage = offset === 0
	const currentPage = Math.floor(offset / limit) + 1

	const handleEmptyResponse = useCallback(() => {
		if (!isFirstPage) {
			setOffset((prev) => Math.max(0, prev - limit))
			setCounterPages((prev) => prev - 1)
			Toast({ type: 'info', message: 'No hay más datos disponibles' })
		}
	}, [isFirstPage, limit])

	const nextPage = useCallback(() => {
		setIsLoading(true)
		setOffset((prev) => prev + limit)
		setCounterPages((prev) => prev + 1)
	}, [limit])

	const prevPage = useCallback(() => {
		if (!isFirstPage) {
			setIsLoading(true)
			setOffset((prev) => Math.max(0, prev - limit))
			setCounterPages((prev) => prev - 1)
		} else {
			Toast({ type: 'info', message: 'Ya estás en la primera página' })
		}
	}, [isFirstPage, limit])

	const setItemsPerPage = useCallback(
		(newLimit: number) => {
			if (newLimit > 0) {
				const currentPage = Math.floor(offset / limit) + 1
				const newOffset = (currentPage - 1) * newLimit

				setIsLoading(true)
				setLimit(newLimit)
				setOffset(newOffset)
			}
		},
		[offset, limit]
	)

	const goToPage = useCallback(
		(page: number) => {
			if (page > 0) {
				if (page < currentPage || page === currentPage + 1) {
					setIsLoading(true)
					setOffset((page - 1) * limit)
					setCounterPages(page)
				} else {
					Toast({
						type: 'warning',
						message: 'Solo puedes avanzar de página en página',
					})
				}
			} else {
				Toast({
					type: 'warning',
					message: 'Número de página inválido',
				})
			}
		},
		[limit, currentPage]
	)

	return {
		offset,
		limit,
		currentPage,
		isFirstPage,
		nextPage,
		prevPage,
		goToPage,
		setItemsPerPage,
		counterPages,
		isLoading,
		setIsLoading,
		handleEmptyResponse,
	}
}
